import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import * as actions from './actions';
import { useActions } from 'hooks/useActions';
import Path from 'routes/Path';
import SearchBar from 'components/SearchBar/SearchBar';
import Sorting from 'components/Sorting/Sorting';
import { Button } from 'components/Button/RoundButton';
import { Spinner } from 'components/Spinner/Spinner';
import { LinkButton } from 'components/Button/RoundButton';
import { Container, Wrapper } from 'components/Layout/Layout';
import { userRoles } from 'constants/users';
import { contestDefaultThumbnail } from 'constants/tests';
import ContestCard from 'components/CMSAllContestCard/CMSAllContestCard';
import Filter from './filter';
import { Header, HeadingContainer } from 'components/PageTitleHeader/styles';
import {
  HeaderContainer,
  ContentContainer,
  LeftContainer,
  RightContainer,
  RightHeaderContainer,
  SortByLabel,
} from 'pages/CustomTestsPage/styles';
import { CardGrid } from 'components/CardContainer/styles';
import { HeadingButtonContainer } from '../cmsAllChallenges/styles';

let defaultFilterOptions = {
  active: true,
  inactive: true,
};

export default React.memo(() => {
  const navigate = useNavigate();
  const { id: challengeId } = useParams();

  const [contestsList, setContestsList] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState({
    value: 'createdAt',
    descendingOrder: true,
  });
  const [filterOptions, setFilterOptions] = useState(defaultFilterOptions);

  const { getContests, createContest, deleteContest, resetUpdatedIds } = useActions(actions);

  let { loading, contests, newContestId, deletedContestId } = useSelector((state) => state.CMSAllContestsReducer);
  let { userRole } = useSelector((state) => state.auth);

  useEffect(() => {
    getContests(challengeId);
  }, [getContests, challengeId]);

  useEffect(() => {
    if (newContestId) {
      resetUpdatedIds();
      navigate(`/cms/contest/${newContestId}`);
    }
  }, [newContestId, resetUpdatedIds, navigate]);

  useEffect(() => {
    if (deletedContestId) {
      resetUpdatedIds();
      toast.success('Contest deleted successfully!');
    }
  }, [deletedContestId, resetUpdatedIds]);

  useEffect(() => {
    setContestsList(contests);
  }, [contests]);

  const sortFunc = useCallback(
    (a, b) => {
      let desc = 1;
      let asc = -1;
      if (String(a[sortBy.value]).toLowerCase() > String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? asc
            : desc
          : sortBy.value === 'createdAt'
            ? desc
            : asc;
      }
      if (String(a[sortBy.value]).toLowerCase() < String(b[sortBy.value]).toLowerCase()) {
        return sortBy.descendingOrder
          ? sortBy.value === 'createdAt'
            ? desc
            : asc
          : sortBy.value === 'createdAt'
            ? asc
            : desc;
      }
      return 0;
    },
    [sortBy]
  );

  const filterContests = useCallback(
    (contest) => {
      if (filterOptions.active && filterOptions.inactive) {
        return true;
      } else if (filterOptions.active && contest.isActive) {
        return true;
      } else if (filterOptions.inactive && !contest.isActive) {
        return true;
      } else {
        return false;
      }
    },
    [filterOptions]
  );

  useEffect(() => {
    setContestsList(contests.filter(searchQueryFilter(searchQuery)).filter(filterContests).sort(sortFunc));
  }, [sortBy, searchQuery, filterOptions, filterContests, sortFunc, contests]);

  const searchQueryFilter = (searchQuery) => (object) =>
    String(object.name).toLowerCase().indexOf(searchQuery.toLowerCase()) > -1;

  return (
    <Wrapper padding="60px 0 0 0" minHeight="100%">
      <Container column>
        <HeadingContainer>
          <Header>Contests</Header>
          <HeadingButtonContainer>
            <LinkButton primaryBlue primaryGreenBorder removeMargin fontSize="12px" href={Path.AllChallenges}>
              <i className="fa fa-arrow-left" style={{ marginRight: '5px' }}></i>All Challenges
            </LinkButton>
            {userRole === userRoles.SUPER_ADMIN && (
              <Button
                primaryBlue
                primaryGreenBorder
                removeMargin
                fontSize="12px"
                onClick={() => createContest(challengeId)}
              >
                <i className="fa fa-plus" style={{ marginRight: '5px' }}></i>New Contest
              </Button>
            )}
          </HeadingButtonContainer>
        </HeadingContainer>

        <ContentContainer>
          <LeftContainer>
            <Filter
              filterOptions={filterOptions}
              defaultFilterOptions={defaultFilterOptions}
              clearFilters={() => setFilterOptions(defaultFilterOptions)}
              filterValues={(values) => setFilterOptions(values)}
            />
          </LeftContainer>
          <RightContainer>
            <HeaderContainer>
              <SearchBar value={searchQuery} onChange={setSearchQuery} />
              <RightHeaderContainer>
                <SortByLabel>Sort by:</SortByLabel>
                <Sorting
                  value={sortBy.value}
                  descendingOrder={sortBy.descendingOrder}
                  options={[
                    { value: 'name', label: 'Name' },
                    { value: 'isActive', label: 'Status' },
                    { value: 'startDate', label: 'Start date' },
                    { value: 'endDate', label: 'End date' },
                  ]}
                  onChange={(value) => setSortBy(value)}
                />
              </RightHeaderContainer>
            </HeaderContainer>
            {loading && <Spinner />}
            <CardGrid>
              {contestsList.length >= 0 &&
                contestsList.map((contest) => (
                  <ContestCard
                    userRole={userRole}
                    key={contest._id}
                    contest={contest}
                    image={contest?.thumbnail ?? contest?.contestBackground ?? contestDefaultThumbnail}
                    deleteContest={() => deleteContest(contest._id)}
                    link={`/cms/contest/${contest._id}`}
                  />
                ))}
            </CardGrid>
          </RightContainer>
        </ContentContainer>
      </Container>
    </Wrapper>
  );
});
